import {action, computed, observable, makeObservable} from "mobx";
import {TopicConfiguration} from "../../config/topic-configuration";
import EsriGraphic from "@arcgis/core/Graphic";
import {Graphic} from "./graphic";
import * as geometryEngine from "@arcgis/core/geometry/geometryEngine";
import {Authentication} from "../../core/authentication";

export type Mode = "raster" | "sketch";
export interface LayerSelection {
    metauid: string;
    layerId?: number;
    layerIds?: number[];
}
export interface LayerFilter {
    metauid: string;
    layerId: number;
    query: string;
}
export interface CurrentLabel {
    layerName: string;
    esriGraphics: __esri.Graphic[];
}
export interface MessageBoxProps {
    type?: MessageBoxType;
    title: string;
    message: string;
    subTitle?: string;
    hasProgress?: boolean;
    percent?: number;
    okButton?: boolean;
    cancelButton?: boolean;
}
export enum MessageBoxType {
    Standard = "standard",
    Info = "info",
    Success = "success",
    Warning = "warning",
    Error = "error"
}

export class AppState {
    @observable topic?: TopicConfiguration;
    @observable subtopic?: TopicConfiguration;
    @observable mode: Mode = "sketch";
    @observable maxObject = 999;
    @observable visibleLayers: Array<string | LayerSelection> = [];
    @observable backgroundLayers: Array<string | LayerSelection> = [];
    @observable mapFilters: Array<LayerFilter> = [];
    @observable currentLabel: CurrentLabel;
    @observable sketchComplete = false;
    @observable sketchCreateTools: string[] = [];
    @observable sketchSelectTools: string[] = [];
    @observable sketchHandler: (graphics: string) => void;
    @observable graphics = observable.array<Graphic>([]);
    @observable authentication: Authentication;
    /**
     * If user is logged in to portal
     * is checked at application startup with IdentityManager
     */
    @observable loggedIn: boolean;
    @observable esriCredential: __esri.Credential | null;
    @observable messageBox = {
        type: MessageBoxType.Standard,
        show: false,
        title: "",
        message: "",
        subTitle: "",
        hasProgress: false,
        percent: 0,
        okButton: true,
        cancelButton: false,
    };
    constructor() {
        makeObservable(this);
    }

    /**
     * Set the current mode. All graphics will be deleted.
     *
     * @param mode The mode by name
     */
    @action setMode(mode: Mode): void {
        this.mode = mode;
        this.graphics.clear();
        if (this.mode === "sketch") {
            this.setVisibleLayers([]);
            this.setMapFilters([]);
        }
    }

    /**
     * Set the list of visible layers by layer ids. The ids are build with the metauid of the service object
     *
     * @param layers A list of layer ids or one single layer
     * @param {number} [layerId]: Only allowed if layers is a string.
     */
    @action setVisibleLayers(layers: string | Array<string | LayerSelection>, layerId?: number | number[]): void {
        if (Array.isArray(layerId) || typeof layerId === "number"){
            if (typeof layers === "string") {
                if (Array.isArray(layerId)){
                    const layer = layers;
                    layers = [];
                    layerId.forEach((lId)=>{
                        if (typeof layers !== "string") {
                            layers.push({metauid: layer, layerId: lId} as LayerSelection);
                        }
                    });
                } else {
                    layers = [{ metauid: layers, layerId}];
                }
            } else {
                console.warn(
                    "Layer ID (" + (Array.isArray(layerId)? layerId.join(", "): layerId) + ") wird ignoriert!");
            }
        }
        this.visibleLayers = Array.isArray(layers) ? layers : [layers];
    }
    /**
     * Set the list of visible background layers by layer ids. The ids are build with the metauid of the service object
     *
     * @param layers A list of layer ids or one single layer
     * @param {number} [layerId]: Only allowed if layers is a string.
     */
    @action setBackgroundLayers(layers: string | Array<string | LayerSelection>, layerId?: number | number[]): void {
        if (Array.isArray(layerId) || typeof layerId === "number"){
            if (typeof layers === "string") {
                if (Array.isArray(layerId)){
                    const layer = layers;
                    layers = [];
                    layerId.forEach((lId)=>{
                        if (typeof layers !== "string") {
                            layers.push({metauid: layer, layerId: lId} as LayerSelection);
                        }
                    });
                } else {
                    layers = [{ metauid: layers, layerId}];
                }
            } else {
                console.warn(
                    "Layer ID (" + (Array.isArray(layerId)? layerId.join(", "): layerId) + ") wird ignoriert!");
            }
        }
        this.backgroundLayers = Array.isArray(layers) ? layers : [layers];
    }

    @action setMapFilters( mapFilters: LayerFilter[]){
        this.mapFilters = mapFilters;
    }
    /**
     *
     *
     *
     */
    @action setSketchCreateTools(toolNames: string[]): void {
        this.sketchCreateTools = Array.isArray(toolNames) ? toolNames : [toolNames];
    }

    /**
     *
     *
     *
     */
    @action setSketchSelectTools(toolNames: string[]): void {
        this.sketchSelectTools = Array.isArray(toolNames) ? toolNames : [toolNames];
    }
    @action setMaxObject(count?: number) {
        if (typeof count === "number") {
            this.maxObject = count;
        } else {
            this.maxObject = 999;
        }
        // shorten graphics array to maxObject from the beginning
        if (this.graphics.length > this.maxObject) {
            this.graphics.splice(0, this.graphics.length - this.maxObject);
            if (this.sketchHandler) {
                this.sketchHandler(this.dumpGraphics());
            }
        }
    }
    /**
     * Add a handler function listening on sketch actions.
     *
     * @param handler: The handler function being used to call after sketch actions.
     */
    @action setSketchHandler(handler: (graphics: string) => void): void {
        this.sketchHandler = handler;
    }
    @action completeSketch(complete?: boolean): void {
        if (typeof complete !== "undefined"){
            this.sketchComplete = complete;
        } else {
            this.sketchComplete = true;
        }
    }
    /**
     * Calculate and return a list of all layers used by the graphic objects.
     *
     * @returns A list of layer names currently in use in the store of graphics.
     */
    @computed get usedLayers(): string[] {
        const layerNames: string[] = [];
        this.graphics.forEach((g: Graphic) => {
            if (!layerNames.includes(g.layerName)) {
                layerNames.push(g.layerName);
                layerNames.push(g.labelLayerName);
            }
        });
        return layerNames;
    }

    /**
     * Calculate and return a list of all layers used by the graphic objects.
     *
     * @returns count of graphics currently in the store.
     */
    @computed get graphicsCount(): number {
        return this.graphics.length;
    }

    /**
     * Add one or multiple graphic objects to the list of graphics.
     * Call the sketch handler if defined.
     *
     * @param layerName: The layer name on which the graphic will be positioned
     * @param esriGraphics: The Esri graphic objects as list of objects.
     * @param callback: Indicates, if a callback to the bind function should be done. Default is true.
     */
    @action addGraphic(layerName: string, esriGraphics: __esri.Graphic | __esri.Graphic[], toggle=false, callback = true): void {
        const graphicList = Array.isArray(esriGraphics) ? esriGraphics : [esriGraphics];
        graphicList.forEach((esriGraphic: __esri.Graphic) => {
            const graphic = new Graphic(layerName, esriGraphic);
            const index = this.graphics.findIndex((g: Graphic) => g.equals(graphic));
            if (index === -1) {
                this.graphics.push(graphic);
            }
            else if (toggle) {
                this.graphics.splice(index, 1);
            }
        });
        // shorten graphics array to maxObject from the beginning
        if (this.graphics.length > this.maxObject) {
            this.graphics.splice(0, this.graphics.length - this.maxObject);
        }
        if (this.sketchHandler && callback) {
            this.sketchHandler(this.dumpGraphics(layerName));
        }
    }

    /**
     * Reset a layer by deleting and resetting all graphic objects.
     *
     * @param layerName: The layerName on which the graphics should be updated
     * @param esriGraphics: The list of graphic objects to update.
     */
    @action resetGraphics(layerName: string, esriGraphics: __esri.Graphic[]): void {
        this.graphics.replace(this.graphics.filter((g: Graphic) => g.layerName !== layerName));
        this.addGraphic(layerName, esriGraphics);
    }
    @action updateGraphicLabel(layerName: string, esriGraphics: __esri.Graphic[]): void {
        this.currentLabel = {layerName, esriGraphics}
    }
    /**
     * Empty a layer by deleting all graphic objects.
     *
     * @param [layerName]: The layerName on which the graphics should be removed
     * @param callback: Indicates, if a callback to the bind function should be done. Default is true.
     */
    @action emptyGraphics(layerName?: string, callback = true): void {
        if (typeof layerName !== "undefined"){
            this.graphics.replace(this.graphics.filter((g: Graphic) => g.layerName !== layerName));
        } else {
            this.graphics.clear();
        }
        if (this.sketchHandler && callback) {
            this.sketchHandler(this.dumpGraphics(layerName));
        }
    }
    /**
     * Add one or multiple Esri graphic JSON objects to the stored graphics.
     * Can be used from external to import some graphics.
     *
     * @param layerName: The name of the layer to add the graphic object
     * @param graphicJson: One graphic JSON or a list of JSON objects
     */
    addGraphicsByJson(layerName: string, graphicJson: JSON | JSON[]): void {
        graphicJson = Array.isArray(graphicJson) ? graphicJson : [graphicJson];
        const graphics: __esri.Graphic[] = [];
        graphicJson.forEach((graphic: JSON) => {
            graphics.push(EsriGraphic.fromJSON(graphic));
        });

        this.addGraphic(layerName, graphics, false, false);
    }

    /**
     * Dump all graphics of a layer as JSON string.
     *
     * @param [layerName]: The layer name to search for all graphics.
     */
    dumpGraphics(layerName?: string): string {
        const graphicsJSON: string[] = [];
        if (typeof layerName !== "undefined") {
            this.graphics.filter((g: Graphic) => g.layerName === layerName).forEach((g: Graphic) => {
                g.graphic.setAttribute("area",
                    geometryEngine.planarArea(g.graphic.geometry as __esri.Polygon, "square-meters"));
                graphicsJSON.push(g.graphic.toJSON());
            });
        } else {
            this.graphics.filter((g: Graphic) => g.layerName.indexOf("_text") <= 0).forEach((g: Graphic) => {
                g.graphic.setAttribute("area",
                    geometryEngine.planarArea(g.graphic.geometry as __esri.Polygon, "square-meters"));
                graphicsJSON.push(g.graphic.toJSON());
            });
        }
        return JSON.stringify(graphicsJSON);
    }

    @action setAuthentication(authentication: Authentication): void {
        this.authentication = authentication;
    }
    @action setLoggedIn(loggedIn: boolean, credential?: __esri.Credential): void {
        this.loggedIn = loggedIn;
        this.esriCredential = credential ? credential : null;
    }
    /**
     * Show a message box
     *
     * @param props MessageBoxProps
     */
    @action showMessageBox(props: MessageBoxProps): void {
        this.messageBox.show = true;

        const {title, message, subTitle, hasProgress, percent} = props;
        this.messageBox.title = title;
        this.messageBox.message = message;
        if (subTitle) {
            this.messageBox.subTitle = subTitle;
        }
        this.messageBox.hasProgress = hasProgress || false;
        if (percent) {
            this.messageBox.percent = percent;
        }

        if (props.hasOwnProperty("type") && props.type !== undefined) {
            this.messageBox.type = props.type;
        }
        if (props.hasOwnProperty("okButton") && props.okButton !== undefined) {
            this.messageBox.okButton = props.okButton;
        }
        if (props.hasOwnProperty("cancelButton") && props.cancelButton !== undefined) {
            this.messageBox.cancelButton = props.cancelButton;
        }
    }

    /**
     * Close the message box
     */
    @action closeMessageBox(): void {
        this.messageBox.type = MessageBoxType.Standard;
        this.messageBox.show = false;
        this.messageBox.title = "";
        this.messageBox.message = "";
        this.messageBox.subTitle = "";
        this.messageBox.hasProgress = false;
        this.messageBox.percent = 0;
        this.messageBox.okButton = true;
        this.messageBox.cancelButton = false;
    }

    /**
     * Update progress on message box
     * Use 'hasProgress = true' to show progress bar on message box
     *
     * @param percent
     */
    @action progressMessageBox(percent: number): void {
        if (percent < 0) {
            percent = 0;
        }
        if (percent > 100) {
            percent = 100;
        }
        this.messageBox.percent = percent;
    }
}
